import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot, doc, updateDoc, getDoc, where, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../lib/firebase';
import { Barber, BarberStatus, QueueTicket, BarberSchedule, BarberDaySchedule } from '../../types';
import { toast } from '@/lib/toast';
import { Mail, Key, User, Copy, Check, Plus, Calendar, Trash2, Camera } from 'lucide-react';
import ImageCropModal from '@/components/modals/ImageCropModal';
import { useAuth } from '../../hooks/useAuth';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<typeof DAYS[number], string> = {
 monday: 'Lunes',
 tuesday: 'Martes',
 wednesday: 'Miércoles',
 thursday: 'Jueves',
 friday: 'Viernes',
 saturday: 'Sábado',
 sunday: 'Domingo',
};

const DEFAULT_SCHEDULE: BarberSchedule = {
 monday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
 tuesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
 wednesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
 thursday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
 friday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
 saturday: { isWorking: true, workStart: '10:00', workEnd: '15:00' },
 sunday: { isWorking: false },
};

interface BarberWithStatus extends Barber {
 currentStatus?: BarberStatus;
 queueLength: number;
}

interface EditingBarber extends Partial<Barber> {
 barberId: string;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const BarbersManagement: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const { t } = useTranslation();
 const { user } = useAuth();
 const navigate = useNavigate();
 const isSuperAdmin = user?.role === 'super_admin';

 const [barbers, setBarbers] = useState<BarberWithStatus[]>([]);
 const [loading, setLoading] = useState(true);
 const [editingBarber, setEditingBarber] = useState<EditingBarber | null>(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [showDeleted, setShowDeleted] = useState(false); // Toggle to show/hide deleted barbers (those with deletedAt)
 // const [resettingPassword, setResettingPassword] = useState<string | null>(null); // TODO: Re-enable when reset password is used
 const [copiedId, setCopiedId] = useState<string | null>(null);
 const [passwordModal, setPasswordModal] = useState<{ barberId: string; email: string; displayName: string } | null>(null);
 const [newPassword, setNewPassword] = useState('');
 const [confirmPassword, setConfirmPassword] = useState('');
 const [updatingPassword, setUpdatingPassword] = useState(false);

 // State for email update (super_admin only)
 const [newEmail, setNewEmail] = useState('');
 const [updatingEmail, setUpdatingEmail] = useState(false);

 // State for delete confirmation
 const [deleteModal, setDeleteModal] = useState<{ barberId: string; displayName: string } | null>(null);
 const [deleting, setDeleting] = useState(false);

 // State for schedule editing
 const [editingSchedule, setEditingSchedule] = useState<BarberSchedule>(DEFAULT_SCHEDULE);

 // State for photo upload in edit modal
 const [uploadingPhoto, setUploadingPhoto] = useState(false);
 const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
 const editPhotoInputRef = useRef<HTMLInputElement>(null);
 const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

 useEffect(() => {
 let unsubscribeBarbers: (() => void) | undefined;
 let unsubscribeStatus: (() => void) | undefined;
 let unsubscribeQueue: (() => void) | undefined;

 let statusQuery = query(collection(db, 'barber_status'));
 let queueQuery = query(
 collection(db, 'queues'),
 where('status', 'in', ['waiting', 'notified', 'arrived'])
 );

 // Apply franchise and branch filters
 if (selectedFranchiseId !== 'all') {
 statusQuery = query(statusQuery, where('franchiseId', '==', selectedFranchiseId));
 queueQuery = query(queueQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 statusQuery = query(statusQuery, where('branchId', '==', selectedBranchId));
 queueQuery = query(queueQuery, where('branchId', '==', selectedBranchId));
 }

 const statusMap = new Map<string, BarberStatus>();
 const queueCounts = new Map<string, number>();
 let barbersList: Barber[] = [];

 const updateBarbers = () => {
 const barbersWithStatus = barbersList.map((barber) => ({
 ...barber,
 currentStatus: statusMap.get(barber.barberId),
 queueLength: queueCounts.get(barber.barberId) || 0,
 }));
 setBarbers(barbersWithStatus);
 setLoading(false);
 };

 // Subscribe to barbers (all barbers - admin needs to see inactive ones to reactivate them)
 let barbersQuery = query(
 collection(db, 'barbers')
 );

 if (selectedFranchiseId !== 'all') {
 barbersQuery = query(barbersQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 barbersQuery = query(barbersQuery, where('branchId', '==', selectedBranchId));
 }

 unsubscribeBarbers = onSnapshot(barbersQuery, (snapshot) => {
 barbersList = snapshot.docs.map((doc) => ({
 barberId: doc.id,
 ...doc.data(),
 })) as Barber[];
 updateBarbers();
 });

 // Subscribe to barber statuses
 unsubscribeStatus = onSnapshot(statusQuery, (statusSnapshot) => {
 statusMap.clear();
 statusSnapshot.docs.forEach((doc) => {
 const status = doc.data() as BarberStatus;
 statusMap.set(status.barberId, status);
 });
 updateBarbers();
 });

 // Subscribe to queue
 unsubscribeQueue = onSnapshot(queueQuery, (queueSnapshot) => {
 queueCounts.clear();
 queueSnapshot.docs.forEach((doc) => {
 const ticket = doc.data() as QueueTicket;
 if (ticket.barberId) {
 queueCounts.set(
 ticket.barberId,
 (queueCounts.get(ticket.barberId) || 0) + 1
 );
 }
 });
 updateBarbers();
 });

 return () => {
 unsubscribeBarbers?.();
 unsubscribeStatus?.();
 unsubscribeQueue?.();
 };
 }, [selectedFranchiseId, selectedBranchId]);

 const handleEdit = (barber: BarberWithStatus) => {
 setEditingBarber({
 barberId: barber.barberId,
 displayName: barber.displayName,
 email: barber.email,
 specialties: barber.specialties,
 bio: barber.bio,
 branchId: barber.branchId,
 isActive: barber.isActive,
 isAvailable: barber.isAvailable,
 photoURL: barber.photoURL,
 userId: barber.userId,
 });
 // Reset new email field when opening edit modal
 setNewEmail('');
 // Load schedule or use default
 setEditingSchedule(barber.schedule || DEFAULT_SCHEDULE);
 // Reset photo preview
 setEditPhotoPreview(barber.photoURL || null);
 };

 const handleEditPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
 const file = event.target.files?.[0];
 if (!file || !editingBarber) return;

 if (!file.type.startsWith('image/')) {
 toast.error('Selecciona una imagen válida');
 return;
 }
 if (file.size > 2 * 1024 * 1024) {
 toast.error('La imagen debe ser menor a 2MB');
 return;
 }

 const reader = new FileReader();
 reader.onload = () => setCropImageSrc(reader.result as string);
 reader.readAsDataURL(file);
 event.target.value = '';
 };

 const uploadCroppedPhoto = async (blob: Blob) => {
 if (!editingBarber) return;
 setCropImageSrc(null);
 setUploadingPhoto(true);
 const toastId = toast.loading('Subiendo foto...');

 try {
 const storageRef = ref(storage, `barbers/${editingBarber.barberId}/photo_${Date.now()}.jpg`);
 const uploadTask = uploadBytesResumable(storageRef, blob);

 uploadTask.on('state_changed',
 (snapshot) => {
 const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
 toast.loading(`Subiendo... ${progress.toFixed(0)}%`, { id: toastId });
 },
 (error) => {
 console.error('Error uploading photo:', error);
 toast.error('Error al subir la foto', { id: toastId });
 setUploadingPhoto(false);
 },
 async () => {
 const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

 await updateDoc(doc(db, 'barbers', editingBarber.barberId), {
 photoURL: downloadURL,
 updatedAt: Timestamp.now(),
 });

 try {
 const barberDoc = await getDoc(doc(db, 'barbers', editingBarber.barberId));
 const userId = barberDoc.data()?.userId;
 if (userId) {
 await updateDoc(doc(db, 'users', userId), {
 photoURL: downloadURL,
 updatedAt: Timestamp.now(),
 });
 }
 } catch (syncErr) {
 console.warn('Could not sync photo to users collection:', syncErr);
 }

 setEditPhotoPreview(downloadURL);
 setEditingBarber(prev => prev ? { ...prev, photoURL: downloadURL } : null);
 toast.success('Foto actualizada', { id: toastId });
 setUploadingPhoto(false);
 }
 );
 } catch (error) {
 console.error('Error in photo upload:', error);
 toast.error('Error al subir la foto', { id: toastId });
 setUploadingPhoto(false);
 }
 };

 const handleScheduleChange = (
 day: typeof DAYS[number],
 field: keyof BarberDaySchedule,
 value: boolean | string
 ) => {
 setEditingSchedule(prev => ({
 ...prev,
 [day]: {
 ...prev[day],
 [field]: value,
 },
 }));
 };

 const handleSave = async () => {
 if (!editingBarber) return;

 try {
 // If super_admin changed email, call Cloud Function to update Auth
 if (isSuperAdmin && newEmail && newEmail !== editingBarber.email) {
 setUpdatingEmail(true);
 try {
 const updateBarberEmailFn = httpsCallable(functions, 'updateBarberEmail');
 const result = await updateBarberEmailFn({
 barberId: editingBarber.barberId,
 newEmail,
 });
 const data = result.data as { success: boolean; message: string };

 if (!data.success) {
 toast.error('No se pudo actualizar el email');
 setUpdatingEmail(false);
 return;
 }
 toast.success('Email actualizado correctamente en Firebase Auth');
 } catch (emailError: any) {
 console.error('Error updating email in Auth:', emailError);
 toast.error(emailError.message || 'Error al actualizar email en Auth');
 setUpdatingEmail(false);
 return;
 }
 setUpdatingEmail(false);
 }

 // Update other fields in Firestore (email is already updated by Cloud Function if changed)
 const barberRef = doc(db, 'barbers', editingBarber.barberId);
 const updateData: Record<string, any> = {
 displayName: editingBarber.displayName,
 specialties: editingBarber.specialties,
 bio: editingBarber.bio,
 branchId: editingBarber.branchId,
 isActive: editingBarber.isActive,
 isAvailable: editingBarber.isAvailable,
 schedule: editingSchedule,
 updatedAt: new Date(),
 };

 // Only update email in Firestore if super_admin didn't change it via Cloud Function
 if (!isSuperAdmin || !newEmail || newEmail === editingBarber.email) {
 updateData.email = editingBarber.email;
 }

 await updateDoc(barberRef, updateData);

 setEditingBarber(null);
 setNewEmail('');
 toast.success('Barber updated successfully');
 } catch (error) {
 console.error('Error updating barber:', error);
 toast.error('Failed to update barber. Please try again.');
 }
 };

 const handleToggleActive = async (barberId: string, currentStatus: boolean) => {
 try {
 const barberRef = doc(db, 'barbers', barberId);
 await updateDoc(barberRef, {
 isActive: !currentStatus,
 updatedAt: new Date(),
 });
 toast.success(`Barber ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
 } catch (error) {
 console.error('Error toggling barber status:', error);
 toast.error('Failed to update barber status. Please try again.');
 }
 };

 const handleToggleAvailable = async (barberId: string, currentStatus: boolean) => {
 try {
 const barberRef = doc(db, 'barbers', barberId);
 await updateDoc(barberRef, {
 isAvailable: !currentStatus,
 updatedAt: new Date(),
 });
 toast.success(`Barber availability updated successfully`);
 } catch (error) {
 console.error('Error toggling barber availability:', error);
 toast.error('Failed to update barber availability. Please try again.');
 }
 };

 // TODO: Re-enable when reset password button is added to UI
 // const handleResetPassword = async (barberId: string, email: string) => {
 // if (!confirm(`¿Enviar email de recuperación de contraseña a ${email}?`)) {
 // return;
 // }
 // setResettingPassword(barberId);
 // try {
 // const resetBarberPasswordFn = httpsCallable(functions, 'resetBarberPassword');
 // const result = await resetBarberPasswordFn({ barberId, email });
 // const data = result.data as { success: boolean; message: string };
 // if (data.success) {
 // toast.success(data.message || 'Email de recuperación enviado');
 // } else {
 // toast.error('No se pudo enviar el email');
 // }
 // } catch (error: any) {
 // console.error('Error resetting password:', error);
 // toast.error(error.message || 'Error al enviar email de recuperación');
 // } finally {
 // setResettingPassword(null);
 // }
 // };

 const handleCopyId = async (id: string, type: 'barber' | 'user') => {
 try {
 await navigator.clipboard.writeText(id);
 setCopiedId(id);
 toast.success(`${type === 'barber' ? 'Barber' : 'User'} ID copiado`);
 setTimeout(() => setCopiedId(null), 2000);
 } catch (error) {
 console.error('Error copying ID:', error);
 toast.error('Error al copiar ID');
 }
 };

 const openPasswordModal = (barber: BarberWithStatus) => {
 setPasswordModal({
 barberId: barber.barberId,
 email: barber.email,
 displayName: barber.displayName,
 });
 setNewPassword('');
 setConfirmPassword('');
 };

 const closePasswordModal = () => {
 setPasswordModal(null);
 setNewPassword('');
 setConfirmPassword('');
 };

 const handleUpdatePassword = async () => {
 if (!passwordModal) return;

 if (newPassword.length < 6) {
 toast.error('La contraseña debe tener al menos 6 caracteres');
 return;
 }

 if (newPassword !== confirmPassword) {
 toast.error('Las contraseñas no coinciden');
 return;
 }

 setUpdatingPassword(true);
 try {
 const updateBarberPasswordFn = httpsCallable(functions, 'updateBarberPassword');
 const result = await updateBarberPasswordFn({
 barberId: passwordModal.barberId,
 newPassword,
 });
 const data = result.data as { success: boolean; message: string };

 if (data.success) {
 toast.success(data.message || 'Contraseña actualizada correctamente');
 closePasswordModal();
 } else {
 toast.error('No se pudo actualizar la contraseña');
 }
 } catch (error: any) {
 console.error('Error updating password:', error);
 toast.error(error.message || 'Error al actualizar contraseña');
 } finally {
 setUpdatingPassword(false);
 }
 };

 const handleDeleteBarber = async () => {
 if (!deleteModal) return;

 setDeleting(true);
 try {
 const deleteBarberFn = httpsCallable(functions, 'deleteBarber');
 const result = await deleteBarberFn({ barberId: deleteModal.barberId });
 const data = result.data as { success: boolean; message: string };

 if (data.success) {
 toast.success('Barbero eliminado correctamente');
 setDeleteModal(null);
 } else {
 toast.error('No se pudo eliminar el barbero');
 }
 } catch (error: any) {
 console.error('Error deleting barber:', error);
 toast.error(error.message || 'Error al eliminar barbero');
 } finally {
 setDeleting(false);
 }
 };

 const getStatusLabel = (status?: BarberStatus) => {
 if (!status) return 'Sin estado';
 switch (status.status) {
 case 'available':
 return 'Disponible';
 case 'busy':
 return 'Atendiendo';
 case 'on_break':
 return 'En descanso';
 case 'offline':
 return 'Desconectado';
 default:
 return 'Sin estado';
 }
 };

 // Get availability label (different from status)
 const getAvailabilityBadge = (barber: BarberWithStatus) => {
 if (!barber.isActive) {
 return { color: 'gray', label: 'Inactivo' };
 }
 if (!barber.isAvailable) {
 return { color: 'red', label: 'No disponible' };
 }
 return { color: 'green', label: 'Aceptando turnos' };
 };

 const filteredBarbers = barbers.filter((barber) => {
 // Hide deleted barbers (those with deletedAt) unless showDeleted is true
 const isDeleted = !!(barber as any).deletedAt;
 if (isDeleted && !showDeleted) {
 return false;
 }
 // Show all non-deleted barbers (active and inactive/deactivated)
 // Then filter by search term
 return (
 (barber.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
 (barber.email || '').toLowerCase().includes(searchTerm.toLowerCase())
 );
 });

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto"></div>
 <p className="mt-4 text-ink-muted">{t('barbers.loading')}</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-2xl font-bold text-ink">{t('barbers.management')}</h2>
 <p className="text-sm text-ink-muted mt-1">{t('barbers.managementDescription')}</p>
 </div>
 <div className="flex items-center gap-4">
 <div className="text-sm text-ink-muted">
 Total: <span className="font-semibold">{barbers.length}</span> barbers
 </div>
 {/* Botón de crear barbero - solo se habilita si hay franquicia y sucursal seleccionadas */}
 <button
 onClick={() => {
 if (selectedFranchiseId !== 'all' && selectedBranchId !== 'all') {
 navigate(`/barbers/new?franchiseId=${selectedFranchiseId}&branchId=${selectedBranchId}`);
 } else {
 toast.error('Selecciona una franquicia y sucursal específica para crear un barbero');
 }
 }}
 disabled={selectedFranchiseId === 'all' || selectedBranchId === 'all'}
 className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 title={selectedFranchiseId === 'all' || selectedBranchId === 'all' ? 'Selecciona una franquicia y sucursal específica' : 'Crear nuevo barbero'}
 >
 <Plus className="w-4 h-4" />
 Nuevo Barbero
 </button>
 </div>
 </div>

 {/* Search and Filters */}
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
 <input
 type="text"
 placeholder={t('barbers.searchPlaceholder')}
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="flex-1 w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink placeholder:text-ink-subtle"
 />
 {/* Toggle to show deleted barbers */}
 <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
 <input
 type="checkbox"
 checked={showDeleted}
 onChange={(e) => setShowDeleted(e.target.checked)}
 className="w-4 h-4 text-danger border-hairline rounded focus:ring-danger"
 />
 <span className="text-sm text-ink-muted">
 Mostrar eliminados
 </span>
 </label>
 </div>

 {/* Barbers List */}
 <div className="space-y-4">
 {filteredBarbers.map((barber) => (
 <div
 key={barber.barberId}
 className="bg-surface border border-hairline rounded-lg p-6 hover:shadow-md transition-shadow"
 >
 <div className="flex items-start justify-between">
 {/* Barber Info */}
 <div className="flex items-start space-x-4 flex-1">
 {barber.photoURL ? (
 <img
 src={barber.photoURL}
 alt={barber.displayName}
 className="w-16 h-16 rounded-full border-2 border-hairline"
 />
 ) : (
 <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center">
 <span className="text-2xl">✂️</span>
 </div>
 )}
 <div className="flex-1">
 <div className="flex items-center flex-wrap gap-2">
 <h3 className="text-lg font-semibold text-ink">
 {barber.displayName}
 </h3>
 {/* Availability badge - primary indicator */}
 {(() => {
 const availability = getAvailabilityBadge(barber);
 return (
 <span
 className={`px-2 py-1 text-xs font-bold rounded-full ${
 availability.color === 'green'
 ? 'bg-success/10 text-success border border-success/30'
 : availability.color === 'red'
 ? 'bg-danger/10 text-danger border border-danger/30'
 : 'bg-surface-2 text-ink-muted border border-hairline'
 }`}
 >
 {availability.label}
 </span>
 );
 })()}
 {/* Current activity status - secondary indicator */}
 {barber.currentStatus?.status === 'busy' && (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent">
 🔄 {getStatusLabel(barber.currentStatus)}
 </span>
 )}
 {barber.currentStatus?.status === 'on_break' && (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent">
 ☕ {getStatusLabel(barber.currentStatus)}
 </span>
 )}
 </div>

 {/* Email con icono */}
 <div className="flex items-center gap-2 mt-2">
 <Mail className="w-4 h-4 text-ink-subtle" />
 <span className="text-sm font-medium text-ink-muted">{barber.email}</span>
 </div>

 {/* IDs copiables */}
 <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
 <div className="flex items-center gap-1 text-ink-subtle">
 <User className="w-3 h-3" />
 <span>User:</span>
 <code className="bg-surface-2 px-1.5 py-0.5 rounded font-mono text-xs">
 {barber.userId?.slice(0, 8)}...
 </code>
 <button
 onClick={() => handleCopyId(barber.userId || '', 'user')}
 className="p-0.5 hover:bg-surface-3 rounded"
 title="Copiar User ID"
 >
 {copiedId === barber.userId ? (
 <Check className="w-3 h-3 text-success" />
 ) : (
 <Copy className="w-3 h-3" />
 )}
 </button>
 </div>
 <div className="flex items-center gap-1 text-ink-subtle">
 <span>Barber:</span>
 <code className="bg-surface-2 px-1.5 py-0.5 rounded font-mono text-xs">
 {barber.barberId?.slice(0, 8)}...
 </code>
 <button
 onClick={() => handleCopyId(barber.barberId || '', 'barber')}
 className="p-0.5 hover:bg-surface-3 rounded"
 title="Copiar Barber ID"
 >
 {copiedId === barber.barberId ? (
 <Check className="w-3 h-3 text-success" />
 ) : (
 <Copy className="w-3 h-3" />
 )}
 </button>
 </div>
 </div>

 {barber.bio && (
 <p className="text-sm text-ink-muted mt-2">{barber.bio}</p>
 )}
 {barber.specialties && barber.specialties.length > 0 && (
 <div className="flex flex-wrap gap-2 mt-2">
 {barber.specialties.map((specialty, index) => (
 <span
 key={index}
 className="px-2 py-1 text-xs bg-info/10 text-info rounded"
 >
 {specialty}
 </span>
 ))}
 </div>
 )}
 <div className="flex items-center space-x-4 mt-3 text-sm text-ink-muted">
 <div>
 Queue: <span className="font-semibold">{barber.queueLength}</span>
 </div>
 </div>
 </div>
 </div>

 {/* Actions */}
 <div className="flex flex-wrap gap-2 mt-4 md:mt-0 md:flex-col md:space-y-2 md:gap-0">
 <button
 onClick={() => handleEdit(barber)}
 className="flex-1 md:flex-none min-w-[100px] px-4 py-3 md:py-2 text-sm font-medium text-info bg-info/10 rounded-lg hover:bg-info/20 transition-colors active:scale-95"
 >
 Editar
 </button>
 {isSuperAdmin && (
 <button
 onClick={() => openPasswordModal(barber)}
 className="flex-1 md:flex-none min-w-[100px] px-4 py-3 md:py-2 text-sm font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors flex items-center justify-center gap-2 active:scale-95"
 >
 <Key className="w-4 h-4" />
 Contraseña
 </button>
 )}
 <button
 onClick={() => handleToggleActive(barber.barberId, barber.isActive)}
 className={`flex-1 md:flex-none min-w-[100px] px-4 py-3 md:py-2 text-sm font-medium rounded-lg transition-colors active:scale-95 ${
 barber.isActive
 ? 'text-danger bg-danger/10 hover:bg-danger/20'
 : 'text-success bg-success/10 hover:bg-success/20'
 }`}
 >
 {barber.isActive ? 'Desactivar' : 'Activar'}
 </button>
 <button
 onClick={() => handleToggleAvailable(barber.barberId, barber.isAvailable)}
 className={`flex-1 md:flex-none min-w-[100px] px-4 py-3 md:py-2 text-sm font-medium rounded-lg transition-colors active:scale-95 ${
 barber.isAvailable
 ? 'text-accent bg-accent/10 hover:bg-accent/20'
 : 'text-success bg-success/10 hover:bg-success/20'
 }`}
 >
 {barber.isAvailable ? 'No Disponible' : 'Disponible'}
 </button>
 {isSuperAdmin && (
 <button
 onClick={() => setDeleteModal({ barberId: barber.barberId, displayName: barber.displayName })}
 className="flex-1 md:flex-none min-w-[100px] px-4 py-3 md:py-2 text-sm font-medium text-danger bg-danger/10 rounded-lg hover:bg-danger/20 transition-colors flex items-center justify-center gap-2 active:scale-95"
 >
 <Trash2 className="w-4 h-4" />
 Eliminar
 </button>
 )}
 </div>
 </div>
 </div>
 ))}

 {filteredBarbers.length === 0 && (
 <div className="text-center py-12 text-ink-subtle">
 <p>{t('barbers.noBarbers')}</p>
 </div>
 )}
 </div>

 {/* Edit Modal */}
 {editingBarber && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center z-50 pt-16 pb-28 md:py-4 px-4 overflow-y-auto">
 <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-11rem)] md:max-h-[85vh] overflow-y-auto">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink">Edit Barber</h3>
 </div>
 <div className="p-6 space-y-4">
 {/* Photo Upload */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Foto de Perfil
 </label>
 <div className="flex items-center gap-4">
 <div className="relative">
 {editPhotoPreview ? (
 <img
 src={editPhotoPreview}
 alt="Foto barbero"
 className="w-20 h-20 rounded-full object-cover border-2 border-hairline"
 />
 ) : (
 <div className="w-20 h-20 rounded-full bg-surface-2 flex items-center justify-center">
 <span className="text-3xl">✂️</span>
 </div>
 )}
 <button
 type="button"
 onClick={() => editPhotoInputRef.current?.click()}
 disabled={uploadingPhoto}
 className="absolute -bottom-1 -right-1 bg-info hover:bg-info/90 text-ink p-1.5 rounded-full shadow-lg transition-colors disabled:bg-surface-3"
 title="Cambiar foto"
 >
 <Camera size={14} />
 </button>
 </div>
 <div className="flex-1">
 <button
 type="button"
 onClick={() => editPhotoInputRef.current?.click()}
 disabled={uploadingPhoto}
 className="px-3 py-1.5 text-sm font-medium text-info bg-info/10 rounded-lg hover:bg-info/20 transition-colors disabled:opacity-50"
 >
 {uploadingPhoto ? 'Subiendo...' : 'Cambiar foto'}
 </button>
 <p className="text-xs text-ink-subtle mt-1">JPG, PNG. Máx 2MB</p>
 </div>
 <input
 ref={editPhotoInputRef}
 type="file"
 accept="image/*"
 onChange={handleEditPhotoUpload}
 className="hidden"
 />
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Display Name
 </label>
 <input
 type="text"
 value={editingBarber.displayName || ''}
 onChange={(e) =>
 setEditingBarber({ ...editingBarber, displayName: e.target.value })
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">Email actual</label>
 <input
 type="email"
 value={editingBarber.email || ''}
 disabled={true}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface-2 text-ink cursor-not-allowed"
 />
 {!isSuperAdmin && (
 <p className="text-xs text-ink-subtle mt-1">
 Solo super_admin puede cambiar el email
 </p>
 )}
 </div>

 {/* New Email field - only for super_admin */}
 {isSuperAdmin && (
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Nuevo Email (opcional)
 </label>
 <input
 type="email"
 value={newEmail}
 onChange={(e) => setNewEmail(e.target.value)}
 placeholder="Dejar vacío para mantener el email actual"
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 />
 <p className="text-xs text-accent mt-1">
 Esto actualizará el email en Firebase Auth y Firestore
 </p>
 </div>
 )}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">Bio</label>
 <textarea
 value={editingBarber.bio || ''}
 onChange={(e) => setEditingBarber({ ...editingBarber, bio: e.target.value })}
 rows={3}
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Specialties (comma-separated)
 </label>
 <input
 type="text"
 value={editingBarber.specialties?.join(', ') || ''}
 onChange={(e) =>
 setEditingBarber({
 ...editingBarber,
 specialties: e.target.value.split(',').map((s) => s.trim()),
 })
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 />
 </div>

 {/* Schedule Section */}
 <div className="border-t border-hairline pt-4 mt-4">
 <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-3">
 <Calendar className="w-4 h-4" />
 Horario de Trabajo
 </label>

 {/* Schedule Grid Header */}
 <div className="hidden md:grid md:grid-cols-12 gap-2 text-xs text-ink-subtle mb-2 px-3">
 <div className="col-span-2">Día</div>
 <div className="col-span-2">Entrada</div>
 <div className="col-span-2">Salida</div>
 <div className="col-span-3">Inicio Pausa</div>
 <div className="col-span-3">Fin Pausa</div>
 </div>

 <div className="space-y-2">
 {DAYS.map((day) => (
 <div key={day} className="p-3 bg-surface-2 rounded-lg">
 {/* Mobile: Stacked layout */}
 <div className="md:hidden space-y-2">
 <div className="flex items-center">
 <input
 type="checkbox"
 id={`edit-${day}-working-mobile`}
 checked={editingSchedule[day]?.isWorking || false}
 onChange={(e) => handleScheduleChange(day, 'isWorking', e.target.checked)}
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus mr-2"
 />
 <label htmlFor={`edit-${day}-working-mobile`} className="text-sm font-semibold text-ink-muted">
 {DAY_LABELS[day]}
 </label>
 </div>
 {editingSchedule[day]?.isWorking && (
 <div className="grid grid-cols-2 gap-2 pl-6">
 <div>
 <label className="text-xs text-ink-subtle">Entrada</label>
 <input
 type="time"
 value={editingSchedule[day]?.workStart || '09:00'}
 onChange={(e) => handleScheduleChange(day, 'workStart', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 <div>
 <label className="text-xs text-ink-subtle">Salida</label>
 <input
 type="time"
 value={editingSchedule[day]?.workEnd || '18:00'}
 onChange={(e) => handleScheduleChange(day, 'workEnd', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 <div>
 <label className="text-xs text-ink-subtle">Inicio Pausa</label>
 <input
 type="time"
 value={editingSchedule[day]?.breakStart || ''}
 onChange={(e) => handleScheduleChange(day, 'breakStart', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 <div>
 <label className="text-xs text-ink-subtle">Fin Pausa</label>
 <input
 type="time"
 value={editingSchedule[day]?.breakEnd || ''}
 onChange={(e) => handleScheduleChange(day, 'breakEnd', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 </div>
 )}
 </div>

 {/* Desktop: Grid layout */}
 <div className="hidden md:grid md:grid-cols-12 gap-2 items-center">
 <div className="col-span-2 flex items-center">
 <input
 type="checkbox"
 id={`edit-${day}-working`}
 checked={editingSchedule[day]?.isWorking || false}
 onChange={(e) => handleScheduleChange(day, 'isWorking', e.target.checked)}
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus mr-2"
 />
 <label htmlFor={`edit-${day}-working`} className="text-sm font-medium text-ink-muted">
 {DAY_LABELS[day]}
 </label>
 </div>
 {editingSchedule[day]?.isWorking ? (
 <>
 <div className="col-span-2">
 <input
 type="time"
 value={editingSchedule[day]?.workStart || '09:00'}
 onChange={(e) => handleScheduleChange(day, 'workStart', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 <div className="col-span-2">
 <input
 type="time"
 value={editingSchedule[day]?.workEnd || '18:00'}
 onChange={(e) => handleScheduleChange(day, 'workEnd', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 />
 </div>
 <div className="col-span-3">
 <input
 type="time"
 value={editingSchedule[day]?.breakStart || ''}
 onChange={(e) => handleScheduleChange(day, 'breakStart', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 placeholder="--:--"
 />
 </div>
 <div className="col-span-3">
 <input
 type="time"
 value={editingSchedule[day]?.breakEnd || ''}
 onChange={(e) => handleScheduleChange(day, 'breakEnd', e.target.value)}
 className="w-full px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
 placeholder="--:--"
 />
 </div>
 </>
 ) : (
 <div className="col-span-10 text-sm text-ink-subtle italic">
 No trabaja este día
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 <p className="text-xs text-ink-subtle mt-2">
 Los días no marcados el barbero no estará disponible para citas
 </p>
 </div>
 </div>
 <div className="p-6 border-t border-hairline flex justify-end space-x-3">
 <button
 onClick={() => {
 setEditingBarber(null);
 setNewEmail('');
 }}
 disabled={updatingEmail}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 onClick={handleSave}
 disabled={updatingEmail}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50 flex items-center gap-2"
 >
 {updatingEmail ? (
 <>
 <span className="animate-spin">⏳</span>
 Actualizando...
 </>
 ) : (
 'Save Changes'
 )}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Password Change Modal */}
 {passwordModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink flex items-center gap-2">
 <Key className="w-5 h-5" />
 Cambiar Contraseña
 </h3>
 <p className="text-sm text-ink-subtle mt-1">
 {passwordModal.displayName} ({passwordModal.email})
 </p>
 </div>
 <div className="p-6 space-y-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Nueva Contraseña
 </label>
 <input
 type="password"
 value={newPassword}
 onChange={(e) => setNewPassword(e.target.value)}
 placeholder="Mínimo 6 caracteres"
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-surface text-ink"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Confirmar Contraseña
 </label>
 <input
 type="password"
 value={confirmPassword}
 onChange={(e) => setConfirmPassword(e.target.value)}
 placeholder="Repite la contraseña"
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-surface text-ink"
 />
 </div>
 {newPassword && confirmPassword && newPassword !== confirmPassword && (
 <p className="text-sm text-danger">Las contraseñas no coinciden</p>
 )}
 {newPassword && newPassword.length > 0 && newPassword.length < 6 && (
 <p className="text-sm text-danger">La contraseña debe tener al menos 6 caracteres</p>
 )}
 </div>
 <div className="p-6 border-t border-hairline flex justify-end space-x-3">
 <button
 onClick={closePasswordModal}
 disabled={updatingPassword}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50"
 >
 Cancelar
 </button>
 <button
 onClick={handleUpdatePassword}
 disabled={updatingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
 className="px-4 py-2 text-sm font-medium text-on-accent bg-accent rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
 >
 {updatingPassword ? (
 <>
 <span className="animate-spin">⏳</span>
 Actualizando...
 </>
 ) : (
 'Actualizar Contraseña'
 )}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Delete Confirmation Modal */}
 {deleteModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink flex items-center gap-2">
 <Trash2 className="w-5 h-5 text-danger" />
 Eliminar Barbero
 </h3>
 </div>
 <div className="p-6">
 <p className="text-ink-muted">
 ¿Estás seguro de que deseas eliminar a <strong>{deleteModal.displayName}</strong>?
 </p>
 <p className="text-sm text-ink-subtle mt-2">
 El barbero será desactivado y ya no aparecerá en las listas activas.
 </p>
 </div>
 <div className="p-6 border-t border-hairline flex justify-end space-x-3">
 <button
 onClick={() => setDeleteModal(null)}
 disabled={deleting}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50"
 >
 Cancelar
 </button>
 <button
 onClick={handleDeleteBarber}
 disabled={deleting}
 className="px-4 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-2"
 >
 {deleting ? (
 <>
 <span className="animate-spin">⏳</span>
 Eliminando...
 </>
 ) : (
 <>
 <Trash2 className="w-4 h-4" />
 Eliminar
 </>
 )}
 </button>
 </div>
 </div>
 </div>
 )}

 <ImageCropModal
 isOpen={!!cropImageSrc}
 imageSrc={cropImageSrc || ''}
 onConfirm={uploadCroppedPhoto}
 onClose={() => setCropImageSrc(null)}
 />
 </div>
 );
};

export default BarbersManagement;
